import { breakpoints } from "./breakpoints.mjs";
import { alpha, createTheme } from "@mui/material/styles";

//#region src/variables.ts
const colors = {
	neutral900: "#111827",
	neutral800: "#1F2937",
	neutral700: "#374151",
	neutral600: "#4B5563",
	neutral500: "#6B7280",
	neutral300: "#D1D5DB",
	neutral200: "#E5E7EB",
	neutral100: "#F3F4F6",
	neutral50: "#F9FAFB",
	neutral0: "#FFFFFF",
	blue700: "#0062FF7A",
	blue600: "#004ECC",
	blue550: "#005FCC",
	blue500: "#0062FF",
	blue400: "#3381FF",
	blue100: "#CCE0FF",
	blue50: "#E5EFFF",
	violet900: "#090116",
	violet800: "#1C0441",
	violet700: "#493666",
	violet500: "#6B21A8",
	violet50: "#FCFCFA",
	red700: "#B70000",
	red600: "#E50000",
	red500: "#EA3333",
	red50: "#FEF2F2",
	pink500: "#F5487F",
	orange700: "#C73B07",
	orange600: "#F05106",
	orange500: "#FF6B0D",
	orange50: "#FFF7ED",
	lightblue500: "#1E85FF",
	lightblue600: "#0663FF",
	lightblue400: "#48ABFF",
	lightblue50: "#EDF7FF",
	green600: "#059669",
	green700: "#047857",
	green500: "#10B981",
	green400: "#65E791",
	green50: "#ECFDF5",
	gray50: "#1C04410A",
	gray100: "#1F29370A",
	mustard400: "#FFAA20",
	mustard50: "#FFF9EB"
};

//#endregion
//#region src/tokens.ts
const spacing = 4;
const getSpace = (multiplier) => multiplier * spacing + "px";
const getFontProps = (fontFamily, fontWeight, fontSize) => ({
	fontFamily,
	fontWeight,
	fontSize
});
const spaces = {
	xxxs: getSpace(.5),
	xxs: getSpace(1),
	xs: getSpace(2),
	sm: getSpace(3),
	md: getSpace(4),
	lg: getSpace(6),
	xl: getSpace(8),
	xxl: getSpace(10),
	xxxl: getSpace(12),
	xxxxl: getSpace(16),
	xxxxxl: getSpace(20)
};
const borderRadius = {
	xxs: getSpace(1),
	xs: getSpace(2),
	sm: getSpace(3),
	md: getSpace(4),
	lg: getSpace(6),
	xl: getSpace(8),
	full: getSpace(90)
};
const backgroundGradients = { aiAssistant: `linear-gradient(187deg, ${colors.violet50} -6.07%, ${colors.blue100} 138.33%);` };
const mobileViewportFullHeight = "calc(var(--vh, 1vh) * 100)";
const palette = {
	common: {
		black: colors.neutral900,
		white: colors.neutral50
	},
	primary: {
		main: colors.blue500,
		dark: colors.blue600,
		light: colors.blue400,
		contrastText: colors.blue50,
		contrast: colors.blue50,
		hover: alpha(colors.blue500, .04),
		outlinedBorder: alpha(colors.blue500, .48),
		selected: alpha(colors.blue500, .08),
		focusVisible: alpha(colors.blue500, .32)
	},
	secondary: {
		main: colors.violet800,
		dark: colors.violet900,
		light: colors.violet700,
		hover: alpha(colors.violet800, .04),
		contrastText: colors.violet50
	},
	error: {
		main: colors.red600,
		dark: colors.red700,
		light: colors.red500,
		contrastText: colors.red50
	},
	warning: {
		main: colors.orange600,
		dark: colors.orange700,
		light: colors.orange500,
		contrastText: colors.orange50
	},
	info: {
		main: colors.lightblue500,
		dark: colors.lightblue600,
		light: colors.lightblue400,
		contrastText: colors.lightblue50
	},
	success: {
		main: colors.green600,
		dark: colors.green700,
		light: colors.green500,
		contrastText: colors.green50
	},
	accent: { pink: colors.pink500 },
	text: {
		primary: colors.violet800,
		secondary: colors.violet700,
		disabled: alpha(colors.violet800, .48),
		focusVisible: alpha(colors.violet800, .32),
		selected: alpha(colors.violet800, .08),
		visited: colors.violet500,
		outlineBorder: colors.blue550
	},
	background: {
		default: colors.neutral100,
		paperElevation0: colors.neutral100,
		paperElevation1: colors.neutral50,
		paperElevation2: colors.neutral0
	},
	action: {
		active: alpha(colors.neutral900, .56),
		hover: alpha(colors.neutral800, .04),
		selected: alpha(colors.neutral900, .08),
		disabled: alpha(colors.neutral900, .4),
		disabledBackground: alpha(colors.neutral900, .12),
		focus: alpha(colors.neutral900, .12)
	},
	components: {
		tooltip: { fill: colors.neutral500 },
		chip: {
			defaultEnabled: colors.neutral200,
			defaultCloseFill: alpha(colors.neutral900, .26)
		},
		alert: {
			info: { color: colors.lightblue600 },
			warning: {
				background: colors.orange50,
				border: alpha(colors.orange600, .48)
			}
		},
		input: { outlined: { enabledBorder: alpha(colors.violet800, .16) } },
		backdrop: { fill: alpha(colors.neutral800, .48) }
	},
	divider: alpha(colors.neutral800, .12),
	elevation: {
		outlined: colors.neutral200,
		enabled: alpha(colors.violet800, .16)
	},
	accordion: { expandIcon: { color: alpha(colors.neutral800, .56) } }
};
const alertDescriptionTypography = {
	...getFontProps("Poppins, sans-serif", 500, "0.875rem"),
	lineHeight: "1.25rem",
	letterSpacing: "0.25px",
	fontStyle: "normal"
};
const typography = {
	fontFamily: "Poppins, sans-serif",
	h1: getFontProps("Borna, sans-serif", 500, "4.5rem"),
	h2: getFontProps("Borna, sans-serif", 400, "2.5rem"),
	h3: getFontProps("Borna, sans-serif", 400, "2rem"),
	h4: getFontProps("Borna, sans-serif", 700, "1.75rem"),
	h5: {
		...getFontProps("Borna, sans-serif", 600, "1.5rem"),
		lineHeight: "2rem"
	},
	h6: {
		...getFontProps("Borna, sans-serif", 600, "1.125rem"),
		lineHeight: "1.5rem",
		letterSpacing: "0.15px"
	},
	subtitle1: getFontProps("Borna, sans-serif", 400, "1rem"),
	subtitle2: {
		...getFontProps("Borna, sans-serif", 400, "0.875rem"),
		lineHeight: "1.5rem",
		letterSpacing: "0.1px"
	},
	subtitle3: {
		...getFontProps("Borna, sans-serif", 600, "0.75rem"),
		lineHeight: "1rem",
		letterSpacing: "0.1px"
	},
	formLabel: {
		...getFontProps("Borna, sans-serif", 500, "1.125rem"),
		lineHeight: "1.75rem",
		letterSpacing: "0.00938rem"
	},
	body1: {
		...getFontProps("Poppins, sans-serif", 300, "1rem"),
		color: palette?.text?.secondary,
		lineHeight: "1.5rem",
		letterSpacing: "0.5px"
	},
	body2: {
		...getFontProps("Poppins, sans-serif", 300, "0.875rem"),
		color: palette?.text?.secondary,
		lineHeight: "1.25rem",
		letterSpacing: "0.25px"
	},
	overline: {
		...getFontProps("Poppins, sans-serif", 400, "0.75rem"),
		letterSpacing: "1.5px",
		lineHeight: "1rem"
	},
	caption: {
		...getFontProps("Poppins, sans-serif", 400, "0.75rem"),
		lineHeight: "1rem",
		letterSpacing: "0.4px"
	},
	caption2: {
		...getFontProps("Poppins, sans-serif", 400, "0.625rem"),
		lineHeight: "1rem",
		letterSpacing: "0.004px"
	},
	alert: { title: {
		...getFontProps("Borna, sans-serif", 500, "1rem"),
		lineHeight: "1.5rem",
		letterSpacing: "0.15px"
	} },
	chip: { label: {
		...getFontProps("Poppins, sans-serif", 500, "0.75rem"),
		lineHeight: "1.125rem",
		letterSpacing: "0.16px"
	} },
	button: {
		small: {
			...getFontProps("Poppins, sans-serif", 500, "0.75rem"),
			lineHeight: "1.375rem",
			letterSpacing: "0.46px"
		},
		alert: {
			...getFontProps("Poppins, sans-serif", 500, "1rem"),
			lineHeight: "1.625rem",
			letterSpacing: "0.46px"
		}
	},
	input: { value: {
		...getFontProps("Poppins, sans-serif", 400, "1rem"),
		lineHeight: "1.5rem",
		letterSpacing: "0.15px"
	} },
	menu: {
		itemDense: {
			...getFontProps("Poppins, sans-serif", 300, "0.75rem"),
			lineHeight: "1.25rem",
			letterSpacing: "0.17px"
		},
		sectionHeader: {
			...getFontProps("Poppins, sans-serif", 400, "0.625rem"),
			lineHeight: "1rem",
			letterSpacing: "0.4px"
		}
	}
};
const topBarHeight = 16 * spacing + "px";
const sideBarWidth = 16 * spacing + "px";
const cardDimensionsMedia = {
	desktop: {
		minWidth: 736,
		maxWidth: 928
	},
	tablet: {
		minWidth: 712,
		maxWidth: 928
	},
	mobile: {
		minWidth: 288,
		maxWidth: 711
	}
};
const cardDimensions = {
	short: { height: 132 },
	long: { height: 280 }
};
const dashboardCardDimensions = {
	width: 218,
	height: 390,
	coverImageHeight: 274,
	coverImageWidth: 194,
	video: {
		width: 276,
		height: 249,
		coverImageHeight: 157,
		coverImageWidth: 252
	}
};
const tooltipArrow = {
	width: 15,
	height: 11
};
const customSizes = {
	assistantNewChatDialog: { desktopWidth: 472 },
	assistantDeleteChatDialog: { desktopWidth: 424 },
	assistantLoomVideoDialog: { desktopWidth: "75%" },
	aiAssistantForm: {
		containerMaxWidth: "1440px",
		innerMaxWidth: "1080px"
	},
	coursesLimitDialog: { desktopWidth: 444 },
	exportDialog: {
		pagesSelectionStageWidth: "960px",
		lernsetSelectionStageWidth: "700px",
		lernsetsListHeight: "274px"
	},
	filtersDialog: {
		desktopWidth: "900px",
		desktopMargin: spaces.xxl,
		tabletMargin: spaces.lg,
		mobileTopMargin: "44px"
	},
	filtersFeedbackDialog: { desktopWidth: 458 },
	createCollectionDialog: { width: 900 },
	readerPage: {
		paddingX: spaces.md,
		paddingY: spaces.md,
		video: {
			maxWidth: "1052px",
			maxHeight: "592px"
		}
	},
	readerDetails: {
		width: "340px",
		headerHeight: "48px",
		publisherLogoHeight: "96px",
		publisherLogoWidth: "128px"
	},
	pagePreviewList: { width: "102px" },
	pdfArea: { paddingX: spaces.md },
	floatingToolbar: {
		height: "48px",
		padding: spaces.xs
	},
	searchInput: {
		width: {
			desktop: "288px",
			tablet: "288px",
			mobile: "259px"
		},
		height: "32px"
	},
	readerToolbarHeight: "65px",
	intercomOffset: { x: "106px" },
	sideNavbar: {
		expandedWidth: "256px",
		collapsedWidth: "48px",
		headerHeight: {
			mobile: "56px",
			desktop: "64px"
		},
		itemHeight: "32px"
	},
	swipeableDrawer: {
		dragIndicatorWidth: "40px",
		dragIndicatorHeight: "6px",
		height: "588px"
	},
	pricingPackage: { width: "368px" },
	gridMargins: spaces.md,
	pricing: { faq: {
		paddingTop: "60px",
		width: {
			desktop: "752px",
			mobile: "100%"
		}
	} },
	paymentMethods: {
		sepaForm: { height: "160px" },
		creditCardForm: { height: {
			desktop: "425px",
			mobile: "465px"
		} }
	},
	paymentInformationContainer: {
		paddingY: "120px",
		width: {
			desktop: "454px",
			mobile: "343px"
		}
	},
	paymentRhythmSelect: { width: {
		desktop: "227px",
		mobile: "171.5px"
	} },
	subjectsDialog: { width: { desktop: "472px" } },
	ownCollectionOverview: {
		watchlistWidth: "268px",
		bodyHeight: "calc(100vh - 176px)"
	},
	emptyStateSubtitleMaxWidth: "448px",
	attachmentsRowHeight: 82,
	paddingBottomLayoutWithIntercomeButton: "80px",
	dialog: {
		maxWidth: "600px",
		maxVideoWidth: "900px",
		maxWidthXs: "444px",
		maxImageHeight: "318px",
		maxImageHeightXs: "235px"
	},
	artifactCard: {
		cardHeight: "200px",
		avatarSize: 40,
		iconSize: 24
	},
	imagePreviewOverlay: {
		buttonSize: 48,
		imageMaxWidth: "95vw",
		imageMaxHeight: "95vh"
	}
};
const boxShadows = {
	elevation1: "0px 1px 2px 0px rgba(17, 24, 39, 0.08)",
	elevation2: "0px 3px 2px 0px rgba(17, 24, 39, 0.08)",
	elevation3: "0px 2px 4px -2px rgba(17, 24, 39, 0.05), 0px 4px 6px -1px rgba(17, 24, 39, 0.10)",
	elevation4: "0px 7px 10px -2px rgba(17, 24, 39, 0.10), 0px 3px 5px -1px rgba(17, 24, 39, 0.05)",
	elevation6: "0px 4px 6px 0px rgba(17, 24, 39, 0.05), 0px 10px 15px -3px rgba(17, 24, 39, 0.10)",
	elevation8: "0px 7px 8px 0px rgba(17, 24, 39, 0.05), 0px 15px 20px -4px rgba(17, 24, 39, 0.10)",
	elevation10: "0px 6px 6px -3px rgba(0, 0, 0, 0.20), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)",
	elevation12: "0px 10px 10px 0px rgba(17, 24, 39, 0.04), 0px 20px 25px -5px rgba(17, 24, 39, 0.10)",
	elevation16: "0px 13px 18px 0px rgba(17, 24, 39, 0.08), 0px 23px 36px -8px rgba(17, 24, 39, 0.10)",
	elevation24: "0px 16px 25px 0px rgba(17, 24, 39, 0.10), 0px 25px 50px -12px rgba(17, 24, 39, 0.25)",
	elevation26: "0px 10px 15px -3px rgba(17, 24, 39, 0.10), 0px 4px 6px 0px rgba(17, 24, 39, 0.05)"
};
const customColors = { lernsetListItem: {
	border: alpha(colors.violet800, .48),
	selectedBorder: alpha(colors.blue500, .48),
	selectedBackground: alpha(colors.blue500, .08)
} };
const fontWeights = {
	light: 300,
	regular: 400,
	medium: 500,
	semibold: 600,
	bold: 700
};
const fontFeatureSettings = { disableLigatures: "'liga' off, 'clig' off" };

//#endregion
//#region src/zIndex.ts
const zIndex = {
	alert: 1500,
	addMenu: 1501,
	searchResultsTab: 2,
	productPageDesignSwitch: 5,
	muiProgress: 1500
};

//#endregion
//#region src/mainTheme.ts
const mainTheme = createTheme({
	palette,
	typography,
	breakpoints: { values: {
		xs: 0,
		sm: 600,
		md: 900,
		lg: 1200,
		xl: 1536,
		collapsedSideNavbar: breakpoints.sidebarBreakpoint,
		mobile: breakpoints.mobile + 1,
		tablet: breakpoints.tablet + 1,
		desktop: breakpoints.desktop + 1
	} },
	components: {
		MuiDialog: { styleOverrides: { root: { "& .MuiPaper-root": { borderRadius: borderRadius.md } } } },
		MuiButton: { styleOverrides: {
			root: { textTransform: "none" },
			contained: {
				boxShadow: boxShadows.elevation4,
				":hover": { boxShadow: boxShadows.elevation8 }
			},
			containedPrimary: { ":hover": { color: palette.primary.contrastText } },
			outlinedPrimary: { ":hover": { borderColor: palette.primary.outlinedBorder } },
			sizeSmall: {
				borderRadius: borderRadius.xs,
				...typography.button.small,
				paddingLeft: spaces.xs,
				paddingRight: spaces.xs,
				paddingTop: spaces.xxs,
				paddingBottom: spaces.xxs
			},
			sizeMedium: { borderRadius: borderRadius.xs },
			sizeLarge: {
				borderRadius: borderRadius.sm,
				fontSize: "1rem",
				fontWeight: 400,
				lineHeight: "1.625rem",
				letterSpacing: "0.46px",
				padding: `${spaces.xs} ${spaces.sm}`
			},
			containedInherit: {
				color: palette.text.primary,
				background: palette.secondary.contrastText,
				":hover": { background: colors.neutral50 }
			},
			outlinedInherit: {
				borderColor: colors.neutral300,
				color: palette.text.secondary,
				":hover": { background: colors.neutral200 }
			}
		} },
		MuiIconButton: {
			styleOverrides: {
				root: { borderRadius: borderRadius.xs },
				sizeLarge: { padding: spaces.sm + "!important" },
				sizeMedium: { padding: spaces.xs },
				sizeSmall: { padding: spaces.xxs }
			},
			variants: [{
				props: { color: "outline" },
				style: {
					borderRadius: borderRadius.md,
					border: `1px solid ${palette.elevation.outlined}`,
					backgroundColor: palette.background.paperElevation2,
					color: palette.secondary.main,
					transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
					"& .MuiTouchRipple-root": { display: "none" },
					"&:hover": {
						backgroundColor: palette.background.paperElevation2,
						boxShadow: boxShadows.elevation3
					},
					"&:focus": {
						backgroundColor: palette.background.paperElevation2,
						borderColor: palette.primary.outlinedBorder
					},
					"&:disabled": {
						backgroundColor: palette.action.hover,
						color: palette.action.disabled
					}
				}
			}, {
				props: {
					color: "outline",
					size: "small"
				},
				style: { borderRadius: borderRadius.sm }
			}]
		},
		MuiMenuItem: { styleOverrides: { root: {
			...getFontProps("Poppins, sans-serif", 400, "1rem"),
			letterSpacing: "0.15px",
			lineHeight: "1.75rem",
			minHeight: spaces.xxl,
			gap: spaces.md,
			alignItems: "center",
			backgroundColor: "transparent!important",
			":hover": {
				borderRadius: spaces.sm,
				backgroundColor: `${colors.gray100}!important`
			}
		} } },
		MuiPopper: { styleOverrides: { root: { zIndex: "1401 !important" } } },
		MuiChip: {
			variants: [{
				props: { size: "xsmall" },
				style: {
					padding: `${spaces.xxxs} 0 !important`,
					height: 20,
					borderRadius: borderRadius.xs,
					"& .MuiChip-label": {
						paddingLeft: spaces.xs,
						paddingRight: spaces.xs
					}
				}
			}],
			styleOverrides: {
				root: { padding: spaces.xxs },
				label: {
					...getFontProps("Poppins, sans-serif", 400, "0.875rem"),
					lineHeight: "1.125rem",
					letterSpacing: "0.16px"
				},
				labelMedium: {
					paddingTop: spaces.xxs,
					paddingBottom: spaces.xxs,
					paddingLeft: spaces.sm,
					paddingRight: spaces.sm
				},
				labelSmall: {
					paddingTop: spaces.xxxs,
					paddingBottom: spaces.xxxs,
					paddingLeft: spaces.xs,
					paddingRight: spaces.xs
				}
			}
		},
		MuiTooltip: { styleOverrides: {
			tooltip: {
				...getFontProps("Poppins, sans-serif", 500, "0.625rem"),
				lineHeight: "0.714rem",
				borderRadius: spaces.xxs,
				backgroundColor: palette.components.tooltip.fill
			},
			arrow: {
				width: tooltipArrow.width,
				height: tooltipArrow.height,
				color: palette.components.tooltip.fill
			},
			tooltipPlacementTop: { ".MuiTooltip-arrow": { marginBottom: -(tooltipArrow.height - 2) + "px !important" } }
		} },
		MuiAlert: { styleOverrides: {
			root: {
				borderRadius: borderRadius.sm,
				border: "1px solid",
				...typography.alert.title,
				zIndex: zIndex.alert
			},
			standardSuccess: {
				borderColor: palette.success.main,
				color: palette.success.main
			},
			standardError: {
				borderColor: palette.error.main,
				color: palette.error.main
			},
			standardWarning: {
				color: palette.warning.main,
				backgroundColor: palette.components.alert.warning.background,
				borderColor: palette.components.alert.warning.border
			},
			standardInfo: {
				borderColor: palette.info.main,
				color: palette.components.alert.info.color
			},
			filledInfo: { "& .MuiAlertTitle-root, & .MuiTypography-root": { color: palette.info.contrastText } }
		} },
		MuiAlertTitle: { styleOverrides: { root: { ...typography.alert.title } } },
		MuiLink: { styleOverrides: {
			root: {
				color: palette.primary.main,
				":hover": { color: palette.primary.main }
			},
			underlineNone: {
				":visited": { color: palette.text.visited },
				"&.Mui-focusVisible": {
					borderRadius: "4px",
					boxShadow: `0px 0px 0px 2px ${palette.text.outlineBorder}`,
					display: "inline-flex",
					outline: "none"
				}
			}
		} },
		MuiFormLabel: { styleOverrides: { root: {
			...getFontProps("Borna, sans-serif", 500, "1.125rem"),
			lineHeight: "1.75rem",
			letterSpacing: "0.15px",
			color: palette.text.primary
		} } },
		MuiTab: { styleOverrides: { root: { ...getFontProps("Poppins, sans-serif", 500, "1rem") } } },
		MuiTypography: { variants: [{
			props: { variant: "menu.itemDense" },
			style: { ...typography.menu.itemDense }
		}] },
		MuiAutocomplete: { styleOverrides: {
			paper: {
				boxShadow: boxShadows.elevation8,
				borderRadius: borderRadius.xs
			},
			listbox: { padding: spaces.xs },
			option: {
				padding: `6px ${spaces.md}`,
				borderRadius: borderRadius.sm,
				...getFontProps("Poppins, sans-serif", 400, "1rem"),
				lineHeight: "1.75rem",
				letterSpacing: "0.15px",
				color: palette.text.primary,
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				display: "list-item!important"
			}
		} }
	}
});
Object.keys(boxShadows).forEach((key) => {
	const elevation = Number.parseInt(key.replace("elevation", ""), 10);
	if (!isNaN(elevation)) mainTheme.shadows[elevation] = boxShadows[key];
});
var mainTheme_default = mainTheme;

//#endregion
export { alertDescriptionTypography, backgroundGradients, borderRadius, boxShadows, breakpoints, cardDimensions, cardDimensionsMedia, colors, customColors, customSizes, dashboardCardDimensions, mainTheme_default as default, mainTheme_default as mainTheme, fontFeatureSettings, fontWeights, getFontProps, mobileViewportFullHeight, palette, sideBarWidth, spaces, spacing, tooltipArrow, topBarHeight, typography, zIndex };
//# sourceMappingURL=index.mjs.map